import type { LiveData } from "./liveTypes";

/** The pictures a post takes; a video post takes videos too. */
export const LIVE_ACCEPT_IMAGES = "image/jpeg,image/png,image/webp";
export const LIVE_ACCEPT = `video/mp4,video/quicktime,video/webm,${LIVE_ACCEPT_IMAGES}`;

/** A change the editor waits on: resolves once the server has it, rejects when refused. */
export type Action = Promise<void> | void;

/**
 * What the Live tab and the post page's banner share: the view model, the
 * uploads in flight, and the moves the page can make. The real stage
 * (useLiveStage) fills it from the distribution read; the dev harness fills it
 * from fixtures. A move the backend cannot make yet is left out, and the page
 * draws no control for it.
 */
export interface LiveStageHandle {
  data: LiveData;
  uploads: { inFlight: number; total: number };
  loading: boolean;
  error: boolean;
  retry?: () => void;
  timeZone: string;
  /** The callback ref for the hidden file input the page renders; the banner's Add files clicks it. */
  registerInput: (input: HTMLInputElement | null) => void;
  openPicker: () => void;
  /** What the file picker offers; unset offers videos and pictures (LIVE_ACCEPT). */
  accept?: string;
  addFiles?: (files: File[]) => void;
  /** Whether a file can be removed now (a placed file on today's backend cannot). */
  canRemove: (fileId: string) => boolean;
  removeFile?: (fileId: string) => void;
  placeInstead?: (trayFileId: string, placedFileId: string) => void;
  addAsChannel?: (fileId: string) => void;
  answer?: (fileId: string) => void;
  tellM7?: (text: string) => void;
  swapFile?: (channelId: string, fileId: string) => Action;
  /** The cover one video channel shows. Null uses the frame from the video. */
  setCover?: (channelId: string, coverDeliverableId: string | null) => Action;
  swapChannel?: (channelId: string, toChannelId: string) => Action;
  reorder?: (channelId: string, from: number, to: number) => void;
  /**
   * A static post's slides, as a whole ordered list: add, remove, rename.
   * Fewer than two make it a single picture again.
   */
  setSlides?: (entries: { id?: string | null; label: string }[]) => Action;
  /** Puts a picture already on the post on a slide, or clears the slide (null). */
  placeOnSlide?: (itemId: string, fileId: string | null) => Action;
  /** Uploads pictures straight onto a slide (the first one; the rest fill empty slides). */
  uploadToSlide?: (itemId: string, files: File[]) => void;
  /** TikTok: the moment of the video used as the cover, in ms. Null lets TikTok pick. */
  setCoverMoment?: (channelId: string, ms: number | null) => Action;
  /** Saves one channel's caption. `title` is YouTube's title. Resolves when the page may close the editor. */
  saveCaption?: (channelId: string, text: string, title?: string) => Action;
  /** The link to a channel's post, once it is up: marks it live and starts its numbers. */
  setPostLink?: (channelId: string, url: string) => Action;
  /** Takes a pasted link back. */
  clearPostLink?: (channelId: string) => Action;
  /** Reads a live post's numbers now. */
  refreshNumbers?: (channelId: string) => void;
  sendKit?: () => void;
  copyKitLink?: () => void;
  /** Publishing: CreativeOS posts this channel at its time (READY → SCHEDULED). */
  schedule?: (channelId: string) => Action;
  /** Every channel that can be scheduled now, at once, with Undo. */
  scheduleAll?: () => void;
  /** Takes a channel off the schedule: it waits to be posted by hand. */
  unschedule?: (channelId: string) => Action;
  /** Tries a failed channel again now. */
  retryPost?: (channelId: string) => void;
  /** Post it yourself: the team posts this channel by hand at its time (true), or CreativeOS does again (false). */
  setSelfPost?: (channelId: string, selfPost: boolean) => Action;
  /** Posts this channel now: it goes on the dispatcher as due, within about a minute. */
  postNow?: (channelId: string) => Action;
  /** Moves one channel's time ("YYYY-MM-DD", "HH:mm", brand time). It stays scheduled if it was. */
  setTime?: (channelId: string, day: string, time: string) => Action;
  /** Connects the platform's account for a channel with none (Settings, Connections). */
  connect?: (channelId: string) => void;
}
